import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const EMPTY = { current_password: "", new_password: "", confirm_password: "" };

export const ChangePasswordDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const close = (v) => {
    if (!v) setForm(EMPTY);
    onOpenChange(v);
  };

  const submit = async () => {
    if (form.new_password.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (form.new_password !== form.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password changed successfully");
      close(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not change password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-white max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Change Password</DialogTitle>
          <DialogDescription>Enter your current password and choose a new one.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Current password</Label>
            <Input type="password" data-testid="current-password-input" value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>New password</Label>
            <Input type="password" data-testid="new-password-input" value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })} placeholder="At least 8 characters" />
          </div>
          <div className="space-y-1.5">
            <Label>Confirm new password</Label>
            <Input type="password" data-testid="confirm-password-input" value={form.confirm_password}
              onChange={(e) => setForm({ ...form, confirm_password: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="submit-change-password-button"
            className="bg-[#2D5A27] hover:bg-[#23471F] text-white">
            {saving ? "Saving..." : "Update Password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
